import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { SignaturePad } from "@/components/SignaturePad";
import { CheckCircle2, FileText, Clock, AlertTriangle, PenLine, Download, Upload } from "lucide-react";

const Shell = ({ children }) => (
  <div className="min-h-screen bg-slate-50">
    <header className="border-b bg-white px-4 py-3"><img src="/sms-logo.jpg" alt="SMS SRL" className="h-9" /></header>
    <main className="mx-auto max-w-3xl px-4 py-6 space-y-5">{children}</main>
  </div>
);

const Notice = ({ icon: Icon, tone, title, text, testid }) => (
  <div className={`rounded-xl border bg-white p-6 text-center space-y-2 ${tone}`} data-testid={testid}>
    <Icon className="mx-auto h-10 w-10" /><h1 className="text-xl font-semibold text-slate-900">{title}</h1><p className="text-sm text-slate-600">{text}</p>
  </div>
);

const DocPages = ({ token, doc }) => (
  <section className="rounded-xl border bg-white p-3 space-y-2" data-testid={`sign-doc-${doc.id}`}>
    <h2 className="flex items-center gap-2 px-1 font-semibold text-slate-900"><FileText className="h-4 w-4 text-sky-600" />{doc.name}</h2>
    {Array.from({ length: doc.pages }, (_, i) => (
      <img key={i} loading="lazy" src={`${API}/sign/${token}/doc/${doc.id}/page/${i + 1}`} alt={`${doc.name} pagina ${i + 1}`} className="w-full border border-slate-200 bg-slate-100 object-contain" style={{ aspectRatio: "595 / 842" }} />
    ))}
  </section>
);

const UploadSigned = ({ token, initial }) => {
  const ref = useRef(null);
  const [list, setList] = useState(initial || []);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const upload = async (files) => {
    setErr("");
    for (const f of files) {
      setBusy(f.name);
      const fd = new FormData(); fd.append("file", f);
      try { const { data } = await api.post(`/sign/${token}/upload`, fd); setList((l) => [...l, { name: data.name, at: new Date().toISOString() }]); }
      catch (e) { setErr(e.response?.status === 413 ? "Il file supera la dimensione massima di 20MB" : formatApiErrorDetail(e.response?.data?.detail)); break; }
    }
    setBusy("");
  };
  return (
    <section className="rounded-xl border-2 border-sky-200 bg-white p-4 space-y-3" data-testid="upload-signed-section">
      <h2 className="flex items-center gap-2 font-semibold text-slate-900"><Upload className="h-4 w-4 text-sky-600" />Carica i documenti firmati</h2>
      <p className="text-sm text-slate-600">Dopo la firma su carta, carica qui il PDF scansionato oppure le foto delle pagine (anche scattandole dal telefono): arrivano subito nella pratica.</p>
      <input ref={ref} type="file" multiple accept="application/pdf,image/jpeg,image/png,image/heic,image/heif" className="hidden"
        onChange={(e) => { upload([...e.target.files]); e.target.value = ""; }} data-testid="upload-signed-input" />
      <Button className="h-11 w-full sm:w-auto" variant="outline" disabled={!!busy} onClick={() => ref.current?.click()} data-testid="upload-signed-btn">
        <Upload className="h-4 w-4 mr-2" />{busy ? `Caricamento ${busy}…` : "Scegli file o scatta foto"}</Button>
      {err && <p className="text-sm text-rose-600" data-testid="upload-signed-error">{err}</p>}
      {list.length > 0 && (
        <ul className="space-y-1 text-sm" data-testid="upload-signed-list">
          {list.map((u, i) => <li key={i} className="flex items-center gap-2 text-emerald-700"><CheckCircle2 className="h-4 w-4 shrink-0" />{u.name}</li>)}
        </ul>
      )}
    </section>
  );
};

export default function SignPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [read, setRead] = useState(false);
  const [sig, setSig] = useState(null);
  const [csig, setCsig] = useState(null);
  const [ksig, setKsig] = useState(null);
  const [signer, setSigner] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { api.get(`/sign/${token}`).then((r) => setInfo(r.data)).catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail))); }, [token]);

  const submit = async () => {
    setBusy(true); setErr("");
    try { await api.post(`/sign/${token}`, { signature: sig, client_signature: csig, coob_signature: ksig, signer_name: signer }); setDone(true); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  if (!info) return <Shell>{err ? <Notice icon={AlertTriangle} tone="text-rose-500" title="Link non valido" text={err} testid="sign-invalid" /> : <p className="text-center text-slate-500">Caricamento…</p>}</Shell>;
  if (["inviato", "caricato"].includes(info.status)) return (
    <Shell>
      <div className="space-y-2" data-testid="print-page">
        <h1 className="text-2xl font-semibold text-slate-900">Documenti da stampare</h1>
        <p className="text-sm text-slate-600">Gentile <b>{info.name}</b>, ecco i documenti già compilati della pratica di <b>{info.client}</b>: stampali e falli firmare al cliente. Link valido fino al {new Date(info.expires_at).toLocaleDateString("it-IT")}.</p>
        <Button asChild className="h-11" data-testid="print-download-btn"><a href={`${API}/sign/${token}/pdf`}><Download className="h-4 w-4 mr-2" />Scarica PDF da stampare</a></Button>
      </div>
      <UploadSigned token={token} initial={info.uploads} />
      {info.documents.map((d) => <DocPages key={d.id} token={token} doc={d} />)}
    </Shell>
  );
  if (done || info.signed_at) return <Shell><Notice icon={CheckCircle2} tone="text-emerald-500" title="Firma completata" text="Grazie! I documenti firmati sono stati inviati all'agenzia. Puoi chiudere questa pagina." testid="sign-done" /></Shell>;
  if (info.status !== "in_attesa") return <Shell><Notice icon={Clock} tone="text-amber-500" title={info.status === "scaduta" ? "Link scaduto" : "Richiesta non più attiva"} text="Contatta l'agenzia per ricevere un nuovo link di firma." testid="sign-inactive" /></Shell>;

  return (
    <Shell>
      <div className="space-y-1" data-testid="sign-page">
        <h1 className="text-2xl font-semibold text-slate-900">Documenti da firmare</h1>
        <p className="text-sm text-slate-600">Gentile <b>{info.name}</b> ({info.role}), leggi i documenti della pratica di <b>{info.client}</b> e firma in fondo alla pagina. Link valido fino al {new Date(info.expires_at).toLocaleDateString("it-IT")}.</p>
      </div>
      {info.documents.map((d) => <DocPages key={d.id} token={token} doc={d} />)}
      <section className="rounded-xl border bg-white p-4 space-y-4">
        <h2 className="flex items-center gap-2 font-semibold text-slate-900"><PenLine className="h-4 w-4 text-sky-600" />La tua firma</h2>
        <label className="flex items-start gap-2 text-sm text-slate-700">
          <Checkbox checked={read} onCheckedChange={(v) => setRead(!!v)} data-testid="sign-read-checkbox" className="mt-0.5" />
          Dichiaro di aver letto i documenti sopra riportati e di sottoscriverli con la firma apposta di seguito.
        </label>
        {info.role === "Convenzionato" && (
          <div className="space-y-1">
            <label className="text-sm font-medium text-slate-700" htmlFor="signer-name">Nome e cognome di chi firma</label>
            <Input id="signer-name" value={signer} onChange={(e) => setSigner(e.target.value)} placeholder="es. Mario Rossi" maxLength={80} data-testid="signer-name-input" />
            <p className="text-xs text-slate-500">Verrà riportato sotto la firma del convenzionato.</p>
          </div>
        )}
        <SignaturePad onChange={setSig} />
        {info.client_pending && (
          <div className="space-y-2 border-t pt-4" data-testid="client-sign-section">
            <h3 className="font-semibold text-slate-900">Firma del cliente <span className="text-xs font-normal text-slate-500">(facoltativa)</span></h3>
            <p className="text-xs text-slate-600">Se <b>{info.client_name}</b> è con te, puoi farlo firmare qui. Altrimenti lascia vuoto: firmerà dal suo link.</p>
            <SignaturePad onChange={setCsig} testid="client-signature-canvas" />
          </div>
        )}
        {info.coob_pending && (
          <div className="space-y-2 border-t pt-4" data-testid="coob-sign-section">
            <h3 className="font-semibold text-slate-900">Firma del coobbligato <span className="text-xs font-normal text-slate-500">(facoltativa)</span></h3>
            <p className="text-xs text-slate-600">Se <b>{info.coob_name}</b> è con te, puoi farlo firmare qui. Altrimenti lascia vuoto: firmerà dal suo link.</p>
            <SignaturePad onChange={setKsig} testid="coob-signature-canvas" />
          </div>
        )}
        {err && <p className="text-sm text-rose-600" data-testid="sign-error">{err}</p>}
        <Button className="w-full h-11" disabled={!read || !sig || busy} onClick={submit} data-testid="sign-submit-btn">{busy ? "Invio in corso…" : "Firma i documenti"}</Button>
        <p className="text-xs text-slate-500">Al momento della firma registriamo data, ora, indirizzo IP e dispositivo a garanzia dell'autenticità.</p>
      </section>
    </Shell>
  );
}
